/**
 * End of round (SPEC §2.4) — the ONLY place a leaderboard appears mid-game.
 *
 * Because a round is a full pass (every player takes exactly one turn), the
 * escalating per-round points are fair by construction whatever the turn order,
 * and this is the moment where that escalation gets announced.
 */
import { useMemo } from 'react';
import type { PlayerId } from '@idgame/protocol';
import { LeaderboardList, Screen, WaitingFor } from '../../components/index.js';
import type { LicensePlayer } from '../../components/index.js';
import {
  useIsHost,
  useLeaderboard,
  useNextRound,
  usePlayers,
  useSelfId,
} from '../../store/index.js';

export function useLicensePlayers(): ReadonlyMap<PlayerId, LicensePlayer> {
  const players = usePlayers();
  return useMemo(() => {
    const map = new Map<PlayerId, LicensePlayer>();
    for (const player of players) {
      map.set(player.id, {
        id: player.id,
        name: player.name,
        photo: player.photo,
        connected: player.connected,
      });
    }
    return map;
  }, [players]);
}

export function RoundLeaderboardScreen() {
  const leaderboard = useLeaderboard();
  const nextRound = useNextRound();
  const players = useLicensePlayers();
  const selfId = useSelfId();
  const isHost = useIsHost();

  if (!leaderboard) {
    return (
      <Screen testId="round-leaderboard-screen">
        <WaitingFor headline="Adding it up." />
      </Screen>
    );
  }

  return (
    <Screen
      eyebrow={<span className="text-green">Round {leaderboard.round} done</span>}
      title="Where everyone stands."
      testId="round-leaderboard-screen"
      bodyClassName="overflow-y-auto"
      footer={
        nextRound !== null ? (
          <p className="text-center text-[0.8125rem] text-ink-soft">
            {/*
              Deliberately no number here. The client must not compute scores
              (CONTRACT.md §3, SPEC §4.2) — the server is authoritative, and a
              stale bundle announcing a value the server does not award is a
              quiet lie that nobody would catch.
            */}
            Round {nextRound} is worth more per pick.{isHost ? ' Starting shortly.' : ''}
          </p>
        ) : (
          <p className="text-center text-[0.8125rem] text-ink-soft">That was the last round.</p>
        )
      }
    >
      <LeaderboardList
        leaderboard={leaderboard}
        players={players}
        selfId={selfId ?? undefined}
      />
    </Screen>
  );
}
